// Beta-reader copies (`CompileService.makeReaderCopies`, SuiteCompile/ReaderCopies.swift; `ReaderRound.mark`,
// SuiteModel): one checkpoint for the round, then a copy per reader in their format, each marked with their name,
// Word copies carrying the round's checkpoint and the reader as custom properties. Storing the round is the app's.
import type * as A from '@automerge/automerge';
import { sha256Hex, trimAll } from '@writing-suite/core';
import { CHECKPOINT_PROPERTY, PROJECT_PROPERTY, READER_PROPERTY } from './docx.ts';
import { prepare, render, type Renderers } from './compile.ts';
import { CompileError } from './presets.ts';
import { fileExtension, type AuthorInfo, type CompiledManuscript } from './types.ts';

export type ReaderFormat = 'pdf' | 'epub' | 'docx';
/** `ReaderRound.Format.presetID`. */
export const READER_PRESETS: Record<ReaderFormat, string> = { pdf: 'reading-copy', epub: 'ebook', docx: 'shunn-docx' };
export const DEFAULT_READER_MESSAGE = "Please don't share";

export interface ReaderRoundInput {
  name: string;
  message?: string;
  returnBy?: Date | null;
  readers: { name: string; format: ReaderFormat }[];
}

/** `ReaderRound.mark(for:)`: "Reading copy for Anna Ng · Please don't share · Notes by Nov 15, 2026". */
export function readerMark(round: ReaderRoundInput, reader: { name: string }, locale?: string): string {
  const parts = [`Reading copy for ${reader.name}`];
  const message = trimAll(round.message ?? DEFAULT_READER_MESSAGE);
  if (message !== '') parts.push(message);
  if (round.returnBy) parts.push(`Notes by ${round.returnBy.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' })}`);
  return parts.join(' · ');
}

export interface ReaderCopy { reader: { name: string; format: ReaderFormat }; bytes: Uint8Array; filename: string; sha256: string }

/** Every reader's copy from one reading of the project, after one checkpoint named after the round. */
export async function makeReaderCopies(docs: Map<string, A.Doc<unknown>>, round: ReaderRoundInput, o: {
  author: AuthorInfo | null; checkpoint: (name: string) => string; now?: Date; renderers?: Renderers; locale?: string;
}): Promise<{ checkpointID: string; copies: ReaderCopy[] }> {
  if (!round.readers.length) throw new CompileError({ type: 'emptyDocument', title: 'A round with no readers' });
  const now = o.now ?? new Date();
  const formats = [...new Set(round.readers.map((r) => r.format))];
  const manuscripts = new Map<ReaderFormat, CompiledManuscript>();
  let projectID = '';
  for (const format of formats) {
    const { manuscript, source } = prepare(docs, { presetId: READER_PRESETS[format], author: o.author, now });
    manuscripts.set(format, manuscript);
    projectID = source.projectID;
  }
  const checkpointID = o.checkpoint(round.name);
  const copies: ReaderCopy[] = [];
  for (const reader of round.readers) {
    const m: CompiledManuscript = { ...manuscripts.get(reader.format)!, readerMark: readerMark(round, reader, o.locale) };
    const properties = { [PROJECT_PROPERTY]: projectID, [CHECKPOINT_PROPERTY]: checkpointID, [READER_PROPERTY]: reader.name };
    const bytes = await render(m, { date: now, properties, renderers: o.renderers });
    const base = `${m.title} — for ${reader.name}`.replaceAll('/', '-');
    copies.push({ reader, bytes, filename: `${base}.${fileExtension(m.preset.format)}`, sha256: sha256Hex(bytes) });
  }
  return { checkpointID, copies };
}
